import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Tooltip from '@radix-ui/react-tooltip'
import * as Popover from '@radix-ui/react-popover'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

const SURFACE =
  'z-50 min-w-[10rem] overflow-hidden rounded-lg border border-border bg-surface p-1 shadow-md'

export function Menu({
  trigger,
  children,
  align = 'end',
}: {
  trigger: ReactNode
  children: ReactNode
  align?: 'start' | 'center' | 'end'
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align={align} sideOffset={4} className={SURFACE}>
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export function MenuItem({
  children,
  onSelect,
  destructive,
  disabled,
  icon,
}: {
  children: ReactNode
  onSelect?: () => void
  destructive?: boolean
  disabled?: boolean
  icon?: ReactNode
}) {
  return (
    <DropdownMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-surface-sunken data-[disabled]:cursor-not-allowed data-[disabled]:text-fg-disabled',
        destructive ? 'text-danger data-[highlighted]:bg-danger-subtle' : 'text-fg',
      )}
    >
      {icon}
      {children}
    </DropdownMenu.Item>
  )
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <div className="px-2 py-1.5 text-2xs font-medium uppercase tracking-wide text-fg-subtle">{children}</div>
}

export function MenuSeparator() {
  return <DropdownMenu.Separator className="my-1 h-px bg-border" />
}

export function MenuCheckboxItem({
  children,
  checked,
  onCheckedChange,
}: {
  children: ReactNode
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <DropdownMenu.CheckboxItem
      checked={checked}
      onCheckedChange={onCheckedChange}
      // Keep the menu open so several columns can be toggled in one visit.
      onSelect={(event) => event.preventDefault()}
      className={cn(
        'flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-fg outline-none',
        'data-[highlighted]:bg-surface-sunken',
      )}
    >
      <span
        className={cn(
          'flex size-3.5 items-center justify-center rounded border',
          checked ? 'border-accent bg-accent text-accent-fg' : 'border-border-strong',
        )}
        aria-hidden
      >
        {checked ? (
          <svg viewBox="0 0 12 12" className="size-2.5 fill-none stroke-current stroke-[2.5]">
            <path d="M2 6.5 4.5 9 10 3.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : null}
      </span>
      {children}
    </DropdownMenu.CheckboxItem>
  )
}

export function TooltipProvider({ children }: { children: ReactNode }) {
  return <Tooltip.Provider delayDuration={300}>{children}</Tooltip.Provider>
}

export function InfoTooltip({ children, label }: { children: ReactNode; label: ReactNode }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          sideOffset={6}
          className="z-50 max-w-xs rounded-md bg-fg px-2 py-1.5 text-xs text-fg-inverse shadow-md"
        >
          {label}
          <Tooltip.Arrow className="fill-fg" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
}

export function PopoverPanel({
  trigger,
  children,
  align = 'start',
  className,
}: {
  trigger: ReactNode
  children: ReactNode
  align?: 'start' | 'center' | 'end'
  className?: string
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={align}
          sideOffset={4}
          className={cn(
            'z-50 rounded-lg border border-border bg-surface p-3 shadow-md',
            className,
          )}
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
